'use client';

/**
 * Alerts Panel Component
 *
 * Aggregated view of errors and alerts with severity levels
 * Works for both system errors (Mothership) and user alerts (Repetix reminders)
 *
 * @example
 * // System errors
 * <AlertsPanel
 *   title="Errors & Alerts"
 *   alerts={[
 *     { id: '1', type: 'error', message: 'API timeout', severity: 'high', app: 'Repetix' },
 *   ]}
 * />
 *
 * // User reminders
 * <AlertsPanel
 *   title="Upcoming Renewals"
 *   alerts={[
 *     { id: '1', type: 'reminder', message: 'Netflix renews in 3 days', severity: 'medium' },
 *   ]}
 * />
 */

import React from 'react';
import {
  AlertTriangle,
  AlertOctagon,
  Database,
  Clock,
  Bot,
  Bell,
  CreditCard,
  ChevronRight,
  CheckCircle,
  LucideIcon,
} from 'lucide-react';

export type AlertType = 'error' | 'warning' | 'info' | 'reminder' | 'db_failure' | 'timeout' | 'agent_failure';
export type AlertSeverity = 'critical' | 'high' | 'medium' | 'low';

export interface AlertItem {
  id: string;
  type: AlertType;
  message: string;
  severity: AlertSeverity;
  app?: string;
  count?: number;
  firstSeen?: Date;
  lastSeen?: Date;
  metadata?: Record<string, unknown>;
}

export interface AlertsPanelProps {
  alerts: AlertItem[];
  title?: string;
  maxItems?: number;
  onViewAll?: () => void;
  onAlertClick?: (alert: AlertItem) => void;
  emptyState?: {
    icon?: string;
    title: string;
    description: string;
  };
}

const typeConfig: Record<AlertType, { icon: LucideIcon; label: string }> = {
  error: { icon: AlertOctagon, label: 'Error' },
  warning: { icon: AlertTriangle, label: 'Warning' },
  info: { icon: Bell, label: 'Info' },
  reminder: { icon: Bell, label: 'Reminder' },
  db_failure: { icon: Database, label: 'Database' },
  timeout: { icon: Clock, label: 'Timeout' },
  agent_failure: { icon: Bot, label: 'Agent' },
};

const severityConfig: Record<AlertSeverity, { color: string; bg: string; border: string }> = {
  critical: { color: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/30' },
  high: { color: 'text-orange-400', bg: 'bg-orange-400/10', border: 'border-orange-400/30' },
  medium: { color: 'text-yellow-400', bg: 'bg-yellow-400/10', border: 'border-yellow-400/30' },
  low: { color: 'text-blue-400', bg: 'bg-blue-400/10', border: 'border-blue-400/30' },
};

function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

function AlertRow({ alert, onClick }: { alert: AlertItem; onClick?: () => void }) {
  const typeInfo = typeConfig[alert.type] || typeConfig.error;
  const severityInfo = severityConfig[alert.severity];
  const Icon = typeInfo.icon;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`w-full flex items-start gap-3 p-3 rounded-lg border ${severityInfo.border} ${severityInfo.bg} transition-colors text-left group ${
        onClick ? 'hover:bg-white/5 cursor-pointer' : ''
      }`}
    >
      <div className="h-8 w-8 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
        <Icon className={`h-4 w-4 ${severityInfo.color}`} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-xs font-medium ${severityInfo.color} uppercase`}>
            {alert.severity}
          </span>
          {alert.app && (
            <>
              <span className="text-xs text-white/40">•</span>
              <span className="text-xs text-violet-400 font-medium">{alert.app}</span>
            </>
          )}
        </div>
        <p className={`text-sm text-white/90 truncate ${onClick ? 'group-hover:text-white' : ''} transition-colors`}>
          {alert.message}
        </p>
        <div className="flex items-center gap-3 mt-1.5">
          {alert.count && alert.count > 1 && (
            <span className="text-xs text-white/40">
              {alert.count} occurrences
            </span>
          )}
          {alert.lastSeen && (
            <span className="text-xs text-white/40">
              Last: {formatTimeAgo(new Date(alert.lastSeen))}
            </span>
          )}
        </div>
      </div>

      {onClick && (
        <ChevronRight className="h-4 w-4 text-white/30 group-hover:text-white/60 transition-colors flex-shrink-0 mt-1" />
      )}
    </button>
  );
}

function EmptyState({ config }: { config: NonNullable<AlertsPanelProps['emptyState']> }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="h-12 w-12 rounded-xl bg-green-400/10 flex items-center justify-center mb-3">
        {config.icon ? (
          <span className="text-2xl">{config.icon}</span>
        ) : (
          <CheckCircle className="h-6 w-6 text-green-400" />
        )}
      </div>
      <p className="text-sm font-medium text-green-400">{config.title}</p>
      <p className="text-xs text-white/40 mt-1">{config.description}</p>
    </div>
  );
}

function SummaryBar({ alerts }: { alerts: AlertItem[] }) {
  const critical = alerts.filter(e => e.severity === 'critical').length;
  const high = alerts.filter(e => e.severity === 'high').length;
  const medium = alerts.filter(e => e.severity === 'medium').length;
  const low = alerts.filter(e => e.severity === 'low').length;

  if (alerts.length === 0) return null;

  return (
    <div className="flex items-center gap-4 px-4 py-2 border-b border-white/5">
      {critical > 0 && (
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-2 rounded-full bg-red-400" />
          <span className="text-xs text-red-400 font-medium">{critical} Critical</span>
        </div>
      )}
      {high > 0 && (
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-2 rounded-full bg-orange-400" />
          <span className="text-xs text-orange-400 font-medium">{high} High</span>
        </div>
      )}
      {medium > 0 && (
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-2 rounded-full bg-yellow-400" />
          <span className="text-xs text-yellow-400 font-medium">{medium} Medium</span>
        </div>
      )}
      {low > 0 && (
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-2 rounded-full bg-blue-400" />
          <span className="text-xs text-blue-400 font-medium">{low} Low</span>
        </div>
      )}
    </div>
  );
}

export function AlertsPanel({
  alerts,
  title = 'Alerts',
  maxItems = 5,
  onViewAll,
  onAlertClick,
  emptyState = {
    title: 'All clear!',
    description: 'No alerts at the moment',
  },
}: AlertsPanelProps) {
  // Sort by severity (critical first) then by lastSeen
  const sortedAlerts = [...alerts].sort((a, b) => {
    const severityOrder: AlertSeverity[] = ['critical', 'high', 'medium', 'low'];
    const aIndex = severityOrder.indexOf(a.severity);
    const bIndex = severityOrder.indexOf(b.severity);
    if (aIndex !== bIndex) {
      return aIndex - bIndex;
    }
    if (a.lastSeen && b.lastSeen) {
      return new Date(b.lastSeen).getTime() - new Date(a.lastSeen).getTime();
    }
    return 0;
  });

  const displayAlerts = sortedAlerts.slice(0, maxItems);

  return (
    <div className="bg-slate-900/60 backdrop-blur-lg border border-white/10 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <AlertTriangle className={`h-4 w-4 ${alerts.length > 0 ? 'text-orange-400' : 'text-white/40'}`} />
          <h3 className="text-sm font-semibold text-white">{title}</h3>
          {alerts.length > 0 && (
            <span className="px-1.5 py-0.5 text-xs font-medium bg-orange-400/20 text-orange-400 rounded-full">
              {alerts.length}
            </span>
          )}
        </div>
        {onViewAll && (
          <button
            type="button"
            onClick={onViewAll}
            className="text-xs text-white/50 hover:text-white/80 transition-colors"
          >
            View All →
          </button>
        )}
      </div>

      {/* Summary bar */}
      <SummaryBar alerts={alerts} />

      {/* Alert list */}
      <div className="max-h-[350px] overflow-y-auto">
        {displayAlerts.length === 0 ? (
          <EmptyState config={emptyState} />
        ) : (
          <div className="p-3 space-y-2">
            {displayAlerts.map((alert) => (
              <AlertRow
                key={alert.id}
                alert={alert}
                onClick={onAlertClick ? () => onAlertClick(alert) : undefined}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
